import { KONE_ROOT_VARS } from './koneColors';

/**
 * Styles for the Checklist Approval plugin.
 *
 * Also includes the small set of shared chrome classes (.card,
 * .detail-container, .dashboard-grid, .dashboard-card, .detail-view-*,
 * .back-button, .form-control, .loading-text) that view3.css assumed it
 * could inherit from view2.css via <link> — since this plugin has no
 * dependency on @internal/backstage-plugin-business-applications, those class
 * definitions are copied here too (verbatim values) rather than imported
 * cross-plugin. If they drift from the business_applications copy over
 * time, that is an acceptable tradeoff for keeping the two plugins
 * independent, per product decision.
 */
export const CHECKLIST_APPROVAL_STYLES = `
:root {
${KONE_ROOT_VARS}
}

/* ---------- Shared chrome (subset needed by this page; see docstring) ---------- */

.detail-container {
  display: grid;
  gap: 16px;
}

.loading-text {
  font-size: 14px;
  color: var(--kone-text-muted);
}

.card,
.primary_card {
  background: var(--kone-surface);
  border: 1px solid var(--kone-border);
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(20, 80, 245, 0.04);
}

.primary_card {
  background: linear-gradient(135deg, var(--kone-sand) 0%, var(--kone-surface) 100%);
}

.card h4,
.primary_card h4 {
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 600;
  color: var(--kone-text);
}

.card p,
.primary_card p {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: var(--kone-text-muted);
}

.evaluation-heading {
  margin-top: 20px;
}

.modules-card h4 {
  margin-bottom: 16px;
}

.dashboard-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

@media (max-width: 720px) {
  .dashboard-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 480px) {
  .dashboard-grid {
    grid-template-columns: 1fr;
  }
}

.dashboard-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 96px;
  padding: 18px 16px;
  border: none;
  border-radius: 12px;
  background: var(--kone-blue);
  color: var(--kone-white);
  font: inherit;
  font-size: 15px;
  font-weight: 600;
  text-align: center;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, filter 0.18s ease;
  box-shadow: 0 2px 6px rgba(20, 80, 245, 0.18);
}

.dashboard-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 18px rgba(20, 80, 245, 0.28);
  filter: brightness(1.06);
}

.dashboard-card:active {
  transform: translateY(0);
  filter: brightness(0.97);
}

.dashboard-card:focus-visible {
  outline: 3px solid rgba(20, 80, 245, 0.4);
  outline-offset: 3px;
}

/* Same specificity-override reasoning as business_applications' details/styles.ts:
   .card p already sets color: var(--kone-text-muted), and .dashboard-grid
   sits inside a div carrying class "card" (see CompliancePage.tsx's card
   modules-card wrapper div), so that descendant selector would otherwise
   win over the plain .dashboard-card { color: white } rule and make the
   label dark text on a blue background. */
.dashboard-grid .dashboard-card-icon,
.dashboard-grid .dashboard-card-label {
  color: var(--kone-white);
}

.dashboard-card-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
}

.dashboard-card-label {
  line-height: 1.3;
}

.detail-view-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  margin-bottom: 16px;
}

.detail-view-card {
  /* .card already supplies background/border/padding/radius/shadow */
}

.back-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: transparent;
  color: var(--kone-blue);
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  padding: 4px 0;
  cursor: pointer;
}

.back-button:hover {
  text-decoration: underline;
}

.detail-view-title {
  margin: 0;
}

.form-control {
  width: 100%;
  max-width: 320px;
  padding: 8px 10px;
  border: 1px solid var(--kone-border);
  border-radius: 8px;
  background: var(--kone-surface);
  color: var(--kone-text);
  font: inherit;
}

.action-button {
  border: none;
  border-radius: 999px;
  padding: 10px 16px;
  cursor: pointer;
  background-color: var(--kone-blue);
  color: var(--kone-white) !important;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  font: inherit;
  font-weight: 600;
}

.action-button:hover {
  background-color: var(--kone-blue);
  opacity: 0.92;
}

.action-button:disabled {
  opacity: 0.5;
  cursor: default;
}

.secondary-button {
  background-color: var(--kone-surface-alt);
  color: var(--kone-text) !important;
  border: 1px solid var(--kone-border);
}

.secondary-button:hover {
  background-color: var(--kone-border);
  opacity: 1;
}

.danger-button {
  background-color: #b42318;
  color: var(--kone-white) !important;
}

.danger-button:hover {
  background-color: #912018;
  opacity: 1;
}

.inline-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 12px;
  align-items: center;
}

/* ------------ Checklist Approval only ----------- */
.checklist-page-container {
  max-width: 1180px;
  margin: 0 auto;
  padding-bottom: 96px;
}

.checklist-card {
  margin-bottom: 20px;
}

.checklist-editor-heading,
.checklist-requirement-heading,
.checklist-dialog-header,
.checklist-dialog-actions,
.checklist-sticky-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.checklist-section-header {
  align-items: flex-start;
}

.checklist-section-header p,
.checklist-editor-heading p {
  margin-bottom: 0;
}

.checklist-dialog-close:focus-visible,
.checklist-form-control:focus-visible {
  outline: 3px solid rgba(20, 80, 245, 0.3);
  outline-offset: 2px;
}

.checklist-table-wrapper {
  overflow-x: auto;
}

.checklist-table {
  border-collapse: collapse;
  width: 100%;
}

.checklist-table th,
.checklist-table td {
  border-bottom: 1px solid var(--kone-border);
  padding: 14px 12px;
  text-align: left;
  vertical-align: middle;
}

.checklist-table th {
  color: var(--kone-text-muted);
  font-size: 0.875rem;
}

.checklist-table-action {
  text-align: right !important;
  white-space: nowrap;
}

.checklist-empty-state {
  background: var(--kone-surface-alt);
  border: 1px solid var(--kone-border);
  border-radius: var(--kone-radius);
  padding: 32px;
  text-align: center;
}

.checklist-empty-state p {
  margin-bottom: 0;
}

.checklist-loading {
  margin: 0;
}

.checklist-error {
  background: rgba(255, 95, 40, 0.1);
  border: 1px solid var(--kone-error);
  border-radius: var(--kone-radius);
  color: var(--kone-text);
  margin: 20px 0;
  padding: 14px 16px;
}

.checklist-error p {
  color: var(--kone-text);
  margin: 0;
}

.checklist-status-badge {
  background: var(--kone-light-blue);
  border: 1px solid var(--kone-border);
  border-radius: 999px;
  color: var(--kone-text-muted);
  font-size: 0.875rem;
  font-weight: 600;
  padding: 7px 12px;
  white-space: nowrap;
}

.checklist-metadata-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  margin-top: 24px;
}

.checklist-metadata-grid > div {
  background: var(--kone-surface-alt);
  border: 1px solid var(--kone-border);
  border-radius: 8px;
  padding: 12px;
  min-width: 0;
}

.checklist-metadata-grid span,
.checklist-metadata-grid strong {
  display: block;
}

.checklist-metadata-grid span {
  color: var(--kone-text-muted);
  font-size: 14px;
  margin-bottom: 4px;
}

.checklist-metadata-grid strong {
  overflow-wrap: anywhere;
}

.checklist-editor-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.checklist-requirement-card {
  margin-bottom: 0;
  padding-top: 14px;
}

.checklist-requirement-heading h3 {
  font-size: 18px;
  margin: 0;
}

.checklist-requirement-heading {
  align-items: center;
}

.checklist-question-count {
  align-self: center;
  background: var(--kone-surface-alt);
  border: 1px solid var(--kone-border);
  border-radius: 999px;
  color: var(--kone-text);
  font-size: 0.8rem;
  font-weight: 600;
  padding: 6px 10px;
  white-space: nowrap;
}

.checklist-question-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 20px;
}

.checklist-form-control {
  box-sizing: border-box;
  display: block;
  font: inherit;
  line-height: 1.45;
  min-height: 88px;
  padding: 10px 12px;
  resize: vertical;
  width: 100%;
  min-width: 0;
  border: 1px solid var(--kone-border);
  border-radius: 8px;
  background: var(--kone-surface);
  color: var(--kone-text);
  overflow-wrap: anywhere;
}

.checklist-form-control--error {
  border: 2px solid var(--kone-error);
}

.checklist-character-count {
  color: var(--kone-text-muted);
  display: block;
  margin-top: 4px;
  text-align: right;
}

.checklist-character-count--error {
  color: var(--kone-error);
  font-weight: 600;
}

.checklist-field-error {
  color: var(--kone-error) !important;
  font-size: 13px !important;
  font-weight: 600;
  margin: 12px 0 0 !important;
}

.checklist-sticky-actions {
  align-items: center;
  background: var(--kone-surface);
  border: 1px solid var(--kone-border);
  border-radius: var(--kone-radius);
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-top: 20px;
  padding: 16px 20px;
}

.checklist-action-group {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.checklist-unsaved-indicator {
  color: var(--kone-warning);
  font-weight: 600;
}

.checklist-saved-indicator {
  color: var(--kone-success);
  font-weight: 600;
}

.checklist-dialog-backdrop {
  align-items: center;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  inset: 0;
  justify-content: center;
  padding: 24px;
  position: fixed;
  z-index: 1300;
}

.checklist-dialog {
  background: var(--kone-surface);
  border: 1px solid var(--kone-border);
  border-radius: var(--kone-radius);
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.24);
  color: var(--kone-text);
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 32px);
  max-width: 900px;
  padding: 20px;
  width: 100%;
}

.checklist-dialog-header h2 {
  color: var(--kone-text);
  margin: 0 0 8px;
}

.checklist-dialog-header p {
  color: var(--kone-text-muted);
  margin: 0;
}

.checklist-dialog-header {
  align-items: flex-start;
}

.checklist-dialog-close {
  appearance: none;
  background: transparent;
  border: none;
  border-radius: 999px;
  color: var(--kone-text);
  cursor: pointer;
  font-size: 26px;
  line-height: 1;
  padding: 5px 9px;
}

.checklist-dialog-close:hover:not(:disabled) {
  background: var(--kone-surface-alt);
}

.checklist-dialog-close:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.checklist-approval-warning {
  background: rgba(255, 160, 35, 0.2);
  border: 1px solid rgba(255, 160, 35, 0.7);
  border-radius: var(--kone-radius);
  color: var(--kone-text);
  margin: 16px 0;
  padding: 14px 16px;
}

.checklist-approval-warning strong {
  color: var(--kone-text);
}

.checklist-approval-warning ul {
  color: var(--kone-text-muted);
  margin: 10px 0 0;
  padding-left: 22px;
}

.checklist-approval-warning li + li {
  margin-top: 5px;
}

.checklist-dialog-preview {
  background: var(--kone-surface);
  border: 1px solid var(--kone-border);
  border-radius: var(--kone-radius);
  color: var(--kone-text);
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

.checklist-preview-requirement {
  border-top: 1px solid var(--kone-border);
  padding: 20px 0;
}

.checklist-preview-requirement:first-of-type {
  border-top: none;
  padding-top: 12px;
}

.checklist-preview-requirement h3 {
  color: var(--kone-text);
  font-size: 16px;
  margin: 0 0 8px;
}

.checklist-preview-requirement p,
.checklist-question-preview {
  color: var(--kone-text-muted);
}

@media (max-width: 800px) {
  .checklist-metadata-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .checklist-editor-heading,
  .checklist-requirement-heading,
  .checklist-sticky-actions {
    align-items: flex-start;
    flex-direction: column;
  }

  .checklist-action-group {
    width: 100%;
  }
}

@media (max-width: 520px) {
  .checklist-metadata-grid {
    grid-template-columns: 1fr;
  }

  .checklist-action-group {
    flex-direction: column;
  }

  .checklist-action-group .action-button {
    width: 100%;
  }
}

.checklist-pending-card {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.checklist-question-display {
  background: transparent;
  border: 1px solid transparent;
  border-radius: 8px;
  color: var(--kone-text);
  cursor: text;
  display: block;
  font: inherit;
  line-height: 1.5;
  padding: 10px 12px;
  text-align: left;
  white-space: pre-wrap;
  width: 100%;
  box-sizing: border-box;
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.checklist-question-display:hover:not(:disabled) {
  background: var(--kone-surface);
  border-color: var(--kone-border);
}

.checklist-question-display:focus-visible {
  border-color: var(--kone-blue);
  outline: 3px solid rgba(20, 80, 245, 0.2);
}

.checklist-page-container > .back-button {
  margin-bottom: 16px;
}

.checklist-editor-summary {
  margin-bottom: 20px;
  padding: 20px;
}

.card.checklist-editor-summary .checklist-editor-heading {
  margin: 0;
}

.card.checklist-editor-summary .checklist-editor-heading h2 {
  margin: 0 0 6px;
}

.checklist-preview-summary {
  margin-bottom: 12px;
}

.checklist-requirement-count-badge {
  background: var(--kone-surface-alt);
  border: 1px solid var(--kone-border);
  border-radius: 999px;
  color: var(--kone-text);
  display: inline-flex;
  font-size: 14px;
  font-weight: 600;
  padding: 6px 12px;
}

.checklist-requirement-description {
  color: var(--kone-text-muted);
  margin: 10px 0 0;
  white-space: pre-wrap;
}

.checklist-question-row {
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr);
  align-items: start;
  gap: 10px;
}

.checklist-question-row,
.checklist-question-content {
  min-width: 0;
}

.checklist-question-number {
  color: var(--kone-text);
  font-size: 14px;
  font-weight: 700;
  line-height: 1.5;
  padding-top: 10px;
  text-align: right;
}

.checklist-pending-header {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: start;
  gap: 20px;
}

.checklist-list-heading h2 {
  margin: 0 0 12px;
}

.checklist-list-heading p {
  margin: 0;
}

.checklist-count-badge {
  align-self: start;
  background: var(--kone-surface-alt);
  border: 1px solid var(--kone-border);
  border-radius: 999px;
  color: var(--kone-text);
  font-size: 13px;
  font-weight: 600;
  padding: 6px 12px;
  white-space: nowrap;
}

@media (max-width: 720px) {
  .checklist-pending-header {
    grid-template-columns: 1fr;
  }

  .checklist-count-badge {
    justify-self: start;
  }
}

.checklist-requirement-card .checklist-questions-heading {
  color: var(--kone-text);
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
  margin: 20px 0 12px;
}

.checklist-save-button:disabled,
.checklist-save-button:disabled:hover {
  background-color: var(--kone-surface-alt);
  border: 1px solid var(--kone-border);
  color: var(--kone-text-muted) !important;
  cursor: default;
  opacity: 0.55;
  transform: none;
}

.checklist-action-error {
  margin: 20px 0 8px;
}

.checklist-requirement-card--error {
  border-color: var(--kone-error);
}

.checklist-question-display--error,
.checklist-question-display--error:hover {
  border-color: var(--kone-error);
}

.checklist-dialog-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 16px;
  min-height: 42px;
}

.checklist-dialog-error {
  color: var(--kone-error);
  font-size: 14px;
  font-weight: 600;
  max-width: 360px;
  text-align: right;
}

.checklist-dialog-actions {
  display: flex;
  flex: 0 0 auto;
  justify-content: flex-end;
  gap: 12px;
  margin: 0;
}

.checklist-operation-button {
  min-width: 154px;
  justify-content: center;
}

.checklist-operation-button--busy,
.checklist-operation-button--busy:disabled,
.checklist-operation-button--busy:hover {
  background-color: var(--kone-blue);
  color: var(--kone-white) !important;
  cursor: wait;
  opacity: 1;
}

.checklist-save-button.checklist-operation-button--busy,
.checklist-save-button.checklist-operation-button--busy:disabled,
.checklist-save-button.checklist-operation-button--busy:hover {
  background-color: var(--kone-surface-alt);
  border: 1px solid var(--kone-border);
  color: var(--kone-text) !important;
  cursor: wait;
  opacity: 1;
}

.checklist-action-group--saving .action-button:disabled {
  opacity: 1;
}

.checklist-requirement-title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  min-width: 0;
  gap: 0.5rem;
}

.checklist-requirement-title h3 {
  min-width: 0;
  margin: 0;
  overflow-wrap: anywhere;
}

.checklist-question-count {
  position: static;
  align-self: center;
  white-space: nowrap;
}

.checklist-question-display:disabled {
  cursor: default;
  opacity: 1;
}

@media (max-width: 1050px) {
  .checklist-requirement-heading {
    grid-template-columns: minmax(0, 1fr) max-content 2rem;
  }

  .review-controls {
    grid-column: 1 / -1;
    width: 100%;
  }
}

@media (max-width: 650px) {
  .checklist-requirement-heading {
    grid-template-columns: minmax(0, 1fr) 2rem;
  }

  .checklist-question-count {
    grid-column: 1;
  }

  .checklist-collapse-button {
    grid-column: 2;
    grid-row: 1;
  }

  .review-controls {
    grid-column: 1 / -1;
  }
}

/* Review progress inside the Requirements metadata cell */

.checklist-review-progress {
  min-width: 250px;
}

.checklist-requirement-total {
  display: block;
  margin-bottom: 8px;
  color: var(--kone-text);
  font-size: 14px;
  line-height: 1.2;
}

.checklist-review-counts {
  display: grid;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.checklist-review-count {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  padding-left: 9px;
  border-left: 3px solid var(--kone-neutral-5);
  color: var(--kone-text-muted);
  font-size: 12px;
  line-height: 1.3;
  position: relative;
  margin: 0;
  padding: 4px 6px 0px 6px;
}

.checklist-review-count strong {
  color: var(--kone-text);
  font-size: 13px;
}

.checklist-review-count--accepted {
  border-left-color: var(--kone-success);
}

.checklist-review-count--regeneration {
  border-left-color: var(--kone-warning);
}

.checklist-review-count--pending {
  border-left-color: var(--kone-neutral-4);
}


/* Requirement card */

.checklist-requirement-card--accepted {
  background: color-mix(
    in srgb,
    var(--kone-green) 18%,
    var(--kone-surface)
  );
}


/* Header order: title, count, review toggle, collapse */

.checklist-requirement-heading {
  display: grid;
  grid-template-columns:
    minmax(180px, 1fr)
    max-content
    32px;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.checklist-requirement-title {
  min-width: 0;
}

.checklist-requirement-title h3 {
  margin: 0;
  color: var(--kone-text);
  overflow-wrap: anywhere;
}

.checklist-question-count {
  position: static;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 28px;
  padding: 3px 10px;
  border: 1px solid var(--kone-border);
  border-radius: 999px;
  background: var(--kone-surface-alt);
  color: var(--kone-text-muted);
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
}


/* Review toggle */
.review-controls {
  display: grid;
  grid-template-columns: max-content max-content;
  align-items: stretch;
  min-height: 38px;
  overflow: hidden;
  margin: 0;
  padding: 0;
  border: 1px solid var(--kone-border);
  border-radius: 8px;
  background: var(--kone-surface);
}

.review-controls label {
  margin: 0;
}

.review-option {
  position: relative;
  display: flex;
  align-items: stretch;
  min-height: 38px;
  margin: 0;
  padding: 0;
  color: var(--kone-text);
  cursor: pointer;
  white-space: nowrap;
}

.review-option + .review-option {
  border-left: 1px solid var(--kone-border);
}

.review-option input {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  opacity: 0;
  pointer-events: none;
}

.review-option span {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 100%;
  min-height: 38px;
  padding: 0 14px;
}

.review-option:not(.review-option--selected):hover span {
  background: var(--kone-surface-alt);
}

.review-option--accepted.review-option--selected span {
  background: color-mix(
    in srgb,
    var(--kone-green) 58%,
    var(--kone-surface)
  );
  color: var(--kone-black);
  font-weight: 600;
}

.review-option--accepted.review-option--selected:hover span {
  background: color-mix(
    in srgb,
    var(--kone-green) 68%,
    var(--kone-surface)
  );
}

.review-option--edit.review-option--selected span {
  background: color-mix(
    in srgb,
    var(--kone-blue) 78%,
    var(--kone-surface)
  );
  color: var(--kone-black);
  font-weight: 600;
}

.review-option--edit.review-option--selected:hover span {
  background: color-mix(
    in srgb,
    var(--kone-blue) 88%,
    var(--kone-surface)
  );
}

/* Collapse control */

.checklist-collapse-button {
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  box-sizing: border-box;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--kone-neutral-2);
  cursor: pointer;
}

.checklist-collapse-button:hover {
  background: var(--kone-surface-alt);
}

.checklist-collapse-button:focus-visible {
  outline: 2px solid var(--kone-neutral-3);
  outline-offset: 2px;
}

.checklist-collapse-icon {
  display: block;
  width: 18px;
  height: 18px;
  transition: transform 120ms ease;
}

.checklist-collapse-icon--collapsed {
  transform: rotate(-90deg);
}


/* Body and regeneration feedback */

.checklist-requirement-body {
  margin-top: 16px;
}

.review-feedback-panel {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 16px 0 20px;
  padding: 16px;
  border: 1px solid var(--kone-neutral-4);
  border-radius: var(--kone-radius);
  background: var(--kone-surface-alt);
}

.review-feedback-panel label {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  color: var(--kone-text);
  font-size: 14px;
  font-weight: 600;
}

.review-feedback-panel label small {
  color: var(--kone-text-muted);
  font-size: 12px;
  font-weight: 400;
}

.review-feedback-input {
  box-sizing: border-box;
  width: 100%;
  margin: 0;
  padding: 10px 12px;
  border: 1px solid var(--kone-border);
  border-radius: 8px;
  background: var(--kone-surface);
  color: var(--kone-text);
  font: inherit;
  resize: vertical;
}

.review-feedback-input:focus {
  border-color: var(--kone-neutral-4);
  outline: 2px solid color-mix(
    in srgb,
    var(--kone-neutral-4) 25%,
    transparent
  );
  outline-offset: 0;
  box-shadow: none;
}


/* Keep the cards from leaving excessive side or bottom space */

.checklist-requirement-card--collapsed {
  padding-bottom: 20px;
}

.checklist-sticky-actions {
  margin-bottom: 0;
}

/* Responsive header */

@media (max-width: 1100px) {
  .checklist-requirement-heading {
    grid-template-columns: minmax(0, 1fr) max-content 32px;
  }

  .review-controls {
    grid-column: 1 / -1;
    justify-self: stretch;
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 700px) {
  .checklist-requirement-heading {
    grid-template-columns: minmax(0, 1fr) 32px;
  }

  .checklist-question-count {
    grid-column: 1;
    justify-self: start;
  }

  .checklist-collapse-button {
    grid-column: 2;
    grid-row: 1;
  }

  .review-controls {
    grid-column: 1 / -1;
  }
}

.checklist-question-display,
.checklist-question-content > textarea.checklist-form-control {
  box-sizing: border-box;
  width: 100%;
  min-height: 42px;
  margin: 0;
  padding: 10px 12px;
  border-radius: 8px;
  font: inherit;
  font-size: 14px;
  line-height: 20px;
}

.checklist-question-content > textarea.checklist-form-control {
  display: block;
  border: 1px solid var(--kone-neutral-4);
  background: var(--kone-surface);
  color: var(--kone-text);
  resize: vertical;
}

.checklist-question-display:hover:not(:disabled) {
  background: var(--kone-surface);
  border-color: var(--kone-border);
}

.checklist-dialog-close:focus-visible,
.checklist-form-control:focus-visible:not(:disabled),
.checklist-question-display:focus-visible:not(:disabled),
.review-feedback-input:focus:not(:disabled),
.checklist-question-content > textarea.checklist-form-control:focus:not(:disabled) {
  border-color: var(--kone-neutral-4);
  outline: 2px solid color-mix(
    in srgb,
    var(--kone-neutral-4) 30%,
    transparent
  );
  outline-offset: 2px;
  box-shadow: none;
}


/* ---------- Unified focus styling ---------- */

/* existing selector updated */
.review-option:focus-within {
  outline: 2px solid var(--kone-neutral-4);
  outline-offset: -2px;
  border-radius: 7px 0 0 7px;
}

.review-option:last-child:focus-within {
  outline: 2px solid var(--kone-neutral-4);
  outline-offset: -2px;
  border-radius: 0 7px 7px 0;
}

/* existing selector updated */
.checklist-collapse-button:focus-visible {
  outline: 2px solid var(--kone-neutral-3);
  outline-offset: 2px;
}


/* ---------- Unified editable controls ---------- */

/* existing selector updated */
.checklist-question-content > textarea.checklist-form-control {
  display: block;
  border: 1px solid var(--kone-border);
  background: var(--kone-surface);
  color: var(--kone-text);
  resize: vertical;
}

/* existing selector updated */
.review-feedback-input {
  box-sizing: border-box;
  width: 100%;
  margin: 0;
  padding: 10px 12px;
  border: 1px solid var(--kone-border);
  border-radius: 8px;
  background: var(--kone-surface);
  color: var(--kone-text);
  font: inherit;
  resize: vertical;
}

/* existing selector updated */
.review-feedback-input:hover:not(:focus):not(:disabled) {
  border-color: var(--kone-neutral-5);
  background: var(--kone-surface);
}


/* ---------- Unified neutral hover styling ---------- */

.checklist-dialog-close:hover:not(:disabled),
.checklist-collapse-button:hover,
.review-option:not(.review-option--selected):hover span {
  background: var(--kone-surface-alt);
}


/* ---------- Unified selected-state hover deltas ---------- */

/* unchanged */
.review-option--accepted.review-option--selected span {
  background: color-mix(
    in srgb,
    var(--kone-green) 78%,
    var(--kone-surface)
  );
}

/* unchanged */
.review-option--accepted.review-option--selected:hover span {
  background: color-mix(
    in srgb,
    var(--kone-green) 88%,
    var(--kone-surface)
  );
}

/* unchanged */
.review-option--regeneration.review-option--selected span {
  background: color-mix(
    in srgb,
    var(--kone-warning) 38%,
    var(--kone-surface)
  );
}

/* updated to match accepted-state +10% hover increase */
.review-option--regeneration.review-option--selected:hover span {
  background: color-mix(
    in srgb,
    var(--kone-warning) 48%,
    var(--kone-surface)
  );
}

.review-status--approved {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  padding: 0.5rem 0.75rem;

  border-radius: 8px;
  color: color-mix(
    in srgb,
    var(--kone-green),
    var(--kone-text) 50%
  );
}

.regeneration-button {
  align-self: flex-start;
  width: auto;
  min-width: 0;
  padding: 0.4rem 0.75rem;
  font-size: 0.875rem;
}

.checklist-question-display:disabled,
.checklist-question-display:disabled:hover,
.checklist-question-display:disabled:focus,
.checklist-question-display:disabled:focus-visible,
.checklist-form-control:disabled,
.checklist-form-control:disabled:hover,
.checklist-form-control:disabled:focus,
.checklist-form-control:disabled:focus-visible,
.checklist-question-content > textarea.checklist-form-control:disabled,
.checklist-question-content > textarea.checklist-form-control:disabled:hover,
.checklist-question-content > textarea.checklist-form-control:disabled:focus,
.checklist-question-content > textarea.checklist-form-control:disabled:focus-visible {
  background: transparent !important;
  border-color: transparent !important;
  outline: none !important;
  box-shadow: none !important;
  cursor: default !important;
  opacity: 1 !important;
  transform: none !important;
}
`;
